/**
 * Pano detay cizimi yardimci fonksiyonlari
 * SVG veya Canvas cizim icin geometri hesaplamalari
 */

/**
 * generateMeterPositions - Sayaclarin grid icerisindeki pozisyonlarini hesaplar
 * @param {Array} meters - Sayac listesi
 * @param {object} grid - Grid bilgileri
 * @param {object} config - PANEL_DETAIL_DEFAULTS
 * @returns {Array} Sayac pozisyonlari [{id, x, y, width, height, phase}]
 */
export function generateMeterPositions(meters, grid, config) {
  const { phase3, phase1 } = config.meter;
  const { thickness } = config.frame;
  const mode = grid.placementMode || 'row';

  const meters3Phase = meters.filter(m => m.phase === 3);
  const meters1Phase = meters.filter(m => m.phase === 1);

  switch (mode) {
    case 'column':
      return _posColumn(meters3Phase, meters1Phase, grid, phase3, phase1, thickness);
    case 'row':
    default:
      return _posRow(meters3Phase, meters1Phase, grid, phase3, phase1, thickness);
  }
}

/**
 * Satir bazli pozisyonlar:
 *  3F satir satir yerlesir, son satirda bos hucrelere 1F girer,
 *  kalan 1F ayni sutun sayisiyla altta devam eder.
 */
function _posRow(m3, m1, grid, p3, p1, t) {
  const positions = [];

  // 3F sayaclar
  m3.forEach((meter, i) => {
    const col = i % grid.cols3;
    const row = Math.floor(i / grid.cols3);
    positions.push({
      id: meter.id, label: meter.label, phase: 3,
      x: t + col * p3.width,
      y: t + row * p3.height,
      width: p3.width, height: p3.height,
    });
  });

  // Son 3F satirindaki bos hucrelere 1F yerlestir
  const filled = grid.filled1FInRow || 0;
  if (filled > 0 && grid.rows3 > 0) {
    const lastRow = grid.rows3 - 1;
    const usedInLastRow = m3.length - lastRow * grid.cols3;
    m1.slice(0, filled).forEach((meter, i) => {
      const col = usedInLastRow + i;
      positions.push({
        id: meter.id, label: meter.label, phase: 1,
        x: t + col * p3.width,
        y: t + lastRow * p3.height,
        width: p1.width, height: p1.height,
      });
    });
  }

  // Kalan 1F sayaclar altta (3F sutun sayisi ve genisligiyle)
  const remaining = m1.slice(filled);
  if (remaining.length > 0) {
    const offsetY = t + grid.rows3 * p3.height;
    const cols = grid.cols3 || grid.cols1 || 1;
    remaining.forEach((meter, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      positions.push({
        id: meter.id, label: meter.label, phase: 1,
        x: t + col * p3.width,
        y: offsetY + row * p1.height,
        width: p1.width, height: p1.height,
      });
    });
  }

  return positions;
}

/**
 * Sutun bazli pozisyonlar:
 *  3F sutunlar solda, 1F sutunlar sagda, yan yana.
 */
function _posColumn(m3, m1, grid, p3, p1, t) {
  const positions = [];

  // 3F sol taraf
  m3.forEach((meter, i) => {
    const col = i % grid.cols3;
    const row = Math.floor(i / grid.cols3);
    positions.push({
      id: meter.id, label: meter.label, phase: 3,
      x: t + col * p3.width,
      y: t + row * p3.height,
      width: p3.width, height: p3.height,
    });
  });

  // 1F sag taraf
  const offsetX = t + grid.cols3 * p3.width;
  m1.forEach((meter, i) => {
    const col = i % grid.cols1;
    const row = Math.floor(i / grid.cols1);
    positions.push({
      id: meter.id, label: meter.label, phase: 1,
      x: offsetX + col * p1.width,
      y: t + row * p1.height,
      width: p1.width, height: p1.height,
    });
  });

  return positions;
}

/**
 * generateDoorRects - Dikey kapak dikdortgenlerini hesaplar
 * Kapaklar tam yukseklikte, genislik maxDoorWidth'e gore esit bolunerek olusturulur
 * @param {object} meterSize - Sayac bolmesi boyutlari {width, height}
 * @param {object} grid - Grid bilgileri (kullanilmiyor, uyumluluk icin)
 * @param {number} doorCount - Kapak sayisi
 * @param {object} config - PANEL_DETAIL_DEFAULTS
 * @returns {Array} Kapak dikdortgenleri [{x, y, width, height, label}]
 */
export function generateDoorRects(meterSize, grid, doorCount, config) {
  const doors = [];

  if (doorCount <= 0) return doors;

  // Dikey kapaklar: tam yukseklik, esit genislikte bolunmus
  const doorWidth = meterSize.width / doorCount;

  for (let i = 0; i < doorCount; i++) {
    doors.push({
      x: i * doorWidth,
      y: 0,
      width: doorWidth,
      height: meterSize.height,
      label: `Kapak ${i + 1}`,
    });
  }

  return doors;
}

/**
 * generateDimensionLines - Olcu cizgilerini hesaplar
 * @param {object} computed - Hesaplanan boyutlar (calculateTotalPanelSize sonucu)
 * @param {object} config - PANEL_DETAIL_DEFAULTS
 * @returns {Array} Olcu cizgileri [{start, end, value, direction, label, layer}]
 */
export function generateDimensionLines(computed, config) {
  const { offset, lineLength } = config.dimensions;
  const { positions } = config.dimensions;
  const lines = [];

  const { meterCompartmentSize, busbarSize, distributionSize, totalSize } = computed;

  // Ust olculer - bolme genislikleri
  if (positions.top) {
    // Sayac bolmesi genisligi
    lines.push({
      startX: 0,
      startY: -offset,
      endX: meterCompartmentSize.width,
      endY: -offset,
      value: meterCompartmentSize.width,
      direction: 'horizontal',
      layer: 'compartment_width',
      label: `${meterCompartmentSize.width.toFixed(config.dimensions.precision)} cm`,
    });

    // Dagitim bolmesi genisligi
    lines.push({
      startX: meterCompartmentSize.width,
      startY: -offset,
      endX: meterCompartmentSize.width + distributionSize.width,
      endY: -offset,
      value: distributionSize.width,
      direction: 'horizontal',
      layer: 'compartment_width',
      label: `${distributionSize.width.toFixed(config.dimensions.precision)} cm`,
    });

    // Toplam genislik (dis katman)
    lines.push({
      startX: 0,
      startY: -(offset + lineLength),
      endX: totalSize.width,
      endY: -(offset + lineLength),
      value: totalSize.width,
      direction: 'horizontal',
      layer: 'total_width',
      label: `${totalSize.width.toFixed(config.dimensions.precision)} cm`,
    });
  }

  // Sol olculer - bolme yukseklikleri
  if (positions.left) {
    // Sayac bolmesi yuksekligi
    lines.push({
      startX: -offset,
      startY: 0,
      endX: -offset,
      endY: meterCompartmentSize.height,
      value: meterCompartmentSize.height,
      direction: 'vertical',
      layer: 'compartment_height',
      label: `${meterCompartmentSize.height.toFixed(config.dimensions.precision)} cm`,
    });

    // Bara bolmesi yuksekligi
    lines.push({
      startX: -offset,
      startY: meterCompartmentSize.height,
      endX: -offset,
      endY: meterCompartmentSize.height + busbarSize.height,
      value: busbarSize.height,
      direction: 'vertical',
      layer: 'compartment_height',
      label: `${busbarSize.height.toFixed(config.dimensions.precision)} cm`,
    });

    // Toplam yukseklik
    lines.push({
      startX: -(offset + lineLength),
      startY: 0,
      endX: -(offset + lineLength),
      endY: totalSize.height,
      value: totalSize.height,
      direction: 'vertical',
      layer: 'total_height',
      label: `${totalSize.height.toFixed(config.dimensions.precision)} cm`,
    });
  }

  return lines;
}

/**
 * getCompartmentLayout - Bolme yerlesim koordinatlarini dondurur
 * @param {object} computed - Hesaplanan boyutlar
 * @param {object} config - PANEL_DETAIL_DEFAULTS
 * @returns {object} Yerlesim bilgileri
 */
export function getCompartmentLayout(computed, config) {
  const { meterCompartmentSize, busbarSize, distributionSize } = computed;
  const { heightMode } = config.distributionCompartment;
  const { widthMode } = config.busbarCompartment;

  // Sayac bolmesi her zaman sol ust
  const meterRect = {
    x: 0,
    y: 0,
    width: meterCompartmentSize.width,
    height: meterCompartmentSize.height,
  };

  // Dagitim bolmesi sag tarafta
  const distRect = {
    x: meterCompartmentSize.width,
    y: 0,
    width: distributionSize.width,
    height: distributionSize.height,
  };

  // Bara bolmesi altta
  let busbarRect;
  if (heightMode === 'METER_PLUS_BUSBAR') {
    // Dagitim tum yuksekligi kapsiyor, bara sadece sayac altinda
    busbarRect = {
      x: 0,
      y: meterCompartmentSize.height,
      width: widthMode === 'BOTH'
        ? meterCompartmentSize.width
        : (widthMode === 'METER_ONLY' ? meterCompartmentSize.width : distributionSize.width),
      height: busbarSize.height,
    };
  } else {
    // Dagitim sadece sayac yuksekliginde, bara tum alti kaplar
    busbarRect = {
      x: 0,
      y: meterCompartmentSize.height,
      width: busbarSize.width,
      height: busbarSize.height,
    };
  }

  return { meterRect, distRect, busbarRect };
}
